import React, { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import { shopSubscriptionService } from "../services/shopSubscriptionService";
import { shopService } from "../services/shopService";
import { planService } from "../../plans/services/planService";
import Modal from "../../../components/common/Modal";
import {
  Store,
  Calendar,
  DollarSign,
  CreditCard,
  CheckCircle2,
  ChevronsUpDown,
  Layers,
} from "lucide-react";

const ManualShopSubscriptionModal = ({ isOpen, onClose }) => {
  const [selectedShop, setSelectedShop] = useState(null);
  const [shopQuery, setShopQuery] = useState("");
  const [planType, setPlanType] = useState("monthly");
  const [planId, setPlanId] = useState("");
  const [amount, setAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const queryClient = useQueryClient();
  const shopId = selectedShop?.id || "";

  const { data: shopsData, isLoading: isLoadingShops } = useQuery({
    queryKey: ["shops-all"],
    queryFn: () => shopService.getShops({ per_page: 500 }),
    enabled: isOpen,
  });

  const { data: plansData } = useQuery({
    queryKey: ["shop-plans"],
    queryFn: () => planService.getPlans({ audience: "shop" }),
    enabled: isOpen,
  });

  // Le montant découle du palier choisi et de la durée.
  React.useEffect(() => {
    const plans = plansData?.data || [];
    const selected = plans.find((p) => String(p.id) === String(planId));
    if (selected) {
      const col = {
        monthly: "price_monthly",
        semi_annual: "price_semi_annual",
        annual: "price_annual",
      }[planType];
      setAmount(String(selected[col] ?? ""));
    }
  }, [planId, planType, plansData]);

  const mutation = useMutation({
    mutationFn: (data) =>
      shopSubscriptionService.createManualSubscription(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["shop-subscriptions"]);
      onClose();
      setSelectedShop(null);
      setShopQuery("");
      setPlanId("");
      setAmount("");
      setPlanType("monthly");
      setPaymentMethod("cash");
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!shopId || !planId || !amount) return;
    mutation.mutate({
      shop_id: shopId,
      plan_id: planId,
      plan_type: planType,
      amount,
      payment_method: paymentMethod,
    });
  };

  const filteredShops = useMemo(() => {
    const q = shopQuery.trim().toLowerCase();
    if (!q) return shopsData?.data || [];
    return (shopsData?.data || []).filter((shop) => {
      const haystack = [
        shop.name,
        shop.user?.firstname,
        shop.user?.lastname,
        shop.user?.phone_number,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [shopsData, shopQuery]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Activation Manuelle Partenariat"
    >
      <div className="p-1">
        <p className="text-sm text-gray-500 mb-6">
          Utilisez ce formulaire pour activer manuellement le partenariat d'une
          boutique. Utile pour les paiements hors-ligne.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Boutique Selection */}
            <div>
              <label className="flex items-center gap-2  ">
                Boutique à activer
              </label>
              <Combobox
                value={selectedShop}
                onChange={setSelectedShop}
                onClose={() => setShopQuery("")}
              >
                <div className="relative">
                  <ComboboxInput
                    className="block w-full border-1 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary h-12 pl-4 pr-10"
                    displayValue={(shop) =>
                      shop
                        ? `${shop.name} — ${shop.user?.firstname ?? ""} (${
                            shop.user?.phone_number ?? ""
                          })`
                        : ""
                    }
                    onChange={(e) => setShopQuery(e.target.value)}
                    placeholder="Rechercher une boutique par nom, propriétaire ou téléphone..."
                  />
                  <ComboboxButton className="absolute right-3 top-1/2 -translate-y-1/2">
                    <ChevronsUpDown size={18} className="text-gray-400" />
                  </ComboboxButton>
                  {/*
                    Pas de prop `anchor` ici : elle rendrait ce menu dans un
                    portail (document.body), hors de `modalRef` — le
                    click-outside de <Modal> fermerait alors toute la modale
                    au premier clic sur un résultat au lieu de sélectionner
                    la boutique. On reste donc en positionnement inline
                    classique (absolute), à l'intérieur de la modale.
                  */}
                  <ComboboxOptions className="absolute z-50 top-full left-0 mt-1 w-full max-h-60 overflow-auto rounded-xl border border-gray-200 bg-white shadow-lg empty:invisible">
                    {filteredShops.length === 0 && (
                      <div className="px-4 py-2 text-sm text-gray-400">
                        Aucune boutique trouvée.
                      </div>
                    )}
                    {filteredShops.map((shop) => (
                      <ComboboxOption
                        key={shop.id}
                        value={shop}
                        className="px-4 py-2 text-sm cursor-pointer data-[focus]:bg-primary/10"
                      >
                        {shop.name} — {shop.user?.firstname} (
                        {shop.user?.phone_number})
                      </ComboboxOption>
                    ))}
                  </ComboboxOptions>
                </div>
              </Combobox>
              {isLoadingShops && (
                <p className="text-[10px] text-primary mt-1 animate-pulse">
                  Chargement des boutiques...
                </p>
              )}
            </div>

            {/* Palier (plan) */}
            <div>
              <label className="flex items-center gap-2 mb-2 ml-1">
                <Layers size={14} /> Palier (plan)
              </label>
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="block w-full text-sm border-1 border-gray-300 rounded-xl focus:ring-2 focus:ring-primary h-12"
                required
              >
                <option value="">Sélectionner un palier...</option>
                {(plansData?.data || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Plan Type */}
              <div>
                <label className="flex items-center gap-2   mb-2 ml-1 ">
                  <Calendar size={14} /> Durée du forfait
                </label>
                <select
                  value={planType}
                  onChange={(e) => setPlanType(e.target.value)}
                  className="block w-full text-sm border-1 border-gray-300   rounded-xl focus:ring-2 focus:ring-primary h-12 transition-all"
                >
                  <option value="monthly">1 Mois (Mensuel)</option>
                  <option value="semi_annual">6 Mois (Semestriel)</option>
                  <option value="annual">1 An (Annuel)</option>
                </select>
              </div>

              {/* Amount */}
              <div>
                <label className="flex items-center gap-2   mb-2 ml-1 ">
                  Montant perçu
                </label>
                <div className="relative">
                  <input
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="Ex: 1000"
                    className="block w-full border-1 border-gray-200 bg-gray-50 rounded-xl focus:ring-2 focus:ring-primary h-12 transition-all pl-4 cursor-not-allowed"
                    required
                    disabled
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-bold ">
                    FCFA
                  </span>
                </div>
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <label className="flex items-center gap-2   mb-2 ml-1 ">
                Mode de règlement
              </label>
              <input
                type="text"
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                placeholder="Ex: Espèces, Virement Orange, etc."
                className="block w-full text-sm border-1 border-gray-300   rounded-xl focus:ring-2 focus:ring-primary h-12 transition-all pl-4"
                required
              />
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-gray-500 bg-white border border-gray-200 rounded-xl hover:  transition-colors"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !shopId || !planId}
              className="flex items-center gap-2 px-6 py-2.5 text-white bg-primary rounded-xl hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
            >
              {mutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Traitement...
                </>
              ) : (
                <>Confirmer l'activation</>
              )}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
};

export default ManualShopSubscriptionModal;
